import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

import {
  CategoryResponse,
  getPerksMainCategories,
} from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/getPerksMainCategories'
import selectPerks, {
  SelectPerksRequest,
} from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/selectPerks'
import deleteCustomCategory from 'core/application/expenses/deleteCustomCategory'
import editCustomCategory from 'core/application/expenses/editCustomCategory'
import insertCustomCategory from 'core/application/expenses/insertCustomCategory'

import DeleteConfirmModal from 'common/components/dataDisplay/DeleteConfirmModal'
import EditableCheckboxButton from 'common/components/dataEntry/components/EditableCheckboxButton'
import Error from 'common/components/dataEntry/formik/FormItemError'
import notification from 'common/components/feedback/Notification'

import Button from '../../../../../../../common/components/general/Button'
import useCommandRequest from '../../../../../../../common/hooks/useCommandRequest'
import useQueryRequest from '../../../../../../../common/hooks/useQueryRequest'
import { BaseComponentProps } from '../../../../../common/productClassWizardModal/Common'

export type SelectPerksProps = {
  id: string
  perksIds: number[]
}

export default (props: BaseComponentProps<SelectPerksProps>) => {
  const { t: translate } = useTranslation()

  const { bindSubmitFormHandler, planPeriodId } = props
  const [categories, , sendGetPerksMainCategories] = useQueryRequest<CategoryResponse[]>(null, () =>
    getPerksMainCategories(props.state.id, planPeriodId),
  )

  const [sendDeletePerkCommand, deleteLoading] = useCommandRequest()
  const [sendCommand] = useCommandRequest()
  const [initialValues, setInitialValues] = useState<SelectPerksRequest>({
    productClassId: props.state.id,
    perksIds: props.state.perksIds,
  })
  const [isAdding, setIsAdding] = useState(false)
  const [deletedCustomPerk, setDeletedCustomPerk] = useState<CategoryResponse | null>(null)

  const onSubmit = async (values: any) => {
    const request = {
      ...values,
      productClassId: props.state.id,
    }
    const result = await sendCommand(() => selectPerks(request))

    if (result.completed) {
      props.goNext()
    } else {
      props.setIsSubmiting(false)
    }
  }

  const checkFormValidity = useCallback(() => {
    props.setIsFormValid(formValidator(translate).isValidSync(initialValues))
  }, [props, initialValues, translate])

  // function handleChange(perksIds, id) {
  //   debugger
  //   let tempArr = [...perksIds];
  //   let found: boolean = false;
  //
  //   for (let i = 0; i < tempArr.length; i++) {
  //     const e = tempArr[i];
  //     if (e === id) {
  //       found = true;
  //       tempArr.splice(i, 1);
  //     }
  //   }
  //
  //   if (!found) {
  //     tempArr.push(id);
  //   }
  //
  //   return tempArr;
  // }

  useEffect(() => {
    checkFormValidity()
  }, [])

  useEffect(() => {
    if (categories && initialValues.perksIds.length === 0) {
      setInitialValues({
        productClassId: props.state.id,
        perksIds: props.state.perksIds,
      })
    }
  }, [categories])

  const onDeleteCustomCategory = async (productClassId: string, customCategoryId: number) => {
    const result = await sendDeletePerkCommand(() =>
      deleteCustomCategory({
        productClassId: productClassId,
        customCategoryId: customCategoryId,
      }),
    )
    if (result.completed) {
      await sendGetPerksMainCategories(() => getPerksMainCategories(props.state.id, planPeriodId))
    }
    return result
  }

  const onEditCustomCategory = async (name: string, productClassId: string, customCategoryId: number) => {
    const result = await sendCommand(() =>
      editCustomCategory({
        name,
        customCategoryId: customCategoryId,
        productClassId: productClassId,
      }),
    )
    if (result.completed) {
      await sendGetPerksMainCategories(() => getPerksMainCategories(props.state.id, planPeriodId))
    }
    return result
  }

  const onInsertCustomCategory = async (name: string, productClassId: string, formik: FormikProps<any>) => {
    const result = await sendCommand(() => insertCustomCategory({ name, relationId: productClassId }))
    if (result.completed) {
      await sendGetPerksMainCategories(() => getPerksMainCategories(props.state.id, planPeriodId))
      formik.setFieldValue('perksIds', [...formik.values.perksIds, result.data])
      //formik.setFieldValue("perksIds", handleChange(formik.values.perksIds, result.data));
      setIsAdding(false)
    }
    return result
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={(values) => {
        const isValid: boolean = formValidator(translate).isValidSync(values)
        props.setHasDirtyData(isValid)
        props.setIsFormValid(isValid)
        return {}
      }}
      validateOnChange={true}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        return (
          <form autoComplete="off" onSubmit={handleSubmit} className="select_class">
            <Row>
              <Col span={24}>
                <h5 className="select_class_info mb-24">
                  {translate('admin-dashboard.benefit-details.class-design.select-perks.header')}
                </h5>
              </Col>
            </Row>
            <div className="mb-8">
              {categories &&
                categories.map((item) => (
                  <EditableCheckboxButton
                    checked={!!formik.values?.perksIds?.find((i) => i === item.id)}
                    onClick={(evt) => {
                      // @ts-ignore
                      const checked = evt.currentTarget.checked
                      if (checked) {
                        formik.setFieldValue('perksIds', [...formik.values.perksIds, item.id])
                      } else {
                        formik.setFieldValue(
                          'perksIds',
                          formik.values.perksIds.filter((x) => x != item.id),
                        )
                      }
                    }}
                    key={item.id}
                    icon={item.icon}
                    name={item.name}
                    className="pb-16"
                    hasActions={item.isCustom}
                    onCancel={() => {}}
                    onSave={async (name) => await onEditCustomCategory(name, formik.values.productClassId, item.id)}
                    onDelete={() => setDeletedCustomPerk(item)}
                  />
                ))}

              {isAdding && (
                <EditableCheckboxButton
                  checked={false}
                  icon="far fa-hand-holding-heart"
                  name=""
                  className="pb-16"
                  hasActions={true}
                  isSubmitting={true}
                  onSave={(name) => onInsertCustomCategory(name, formik.values.productClassId, formik)}
                  onCancel={() => setIsAdding(false)}
                />
              )}
            </div>

            <h5 className="select_class_info mb-24">
              {translate('admin-dashboard.benefit-details.class-design.select-perks.custom')}
            </h5>
            <Button disabled={isAdding} type="primary" ghost icon="far fa-plus" onClick={() => setIsAdding(true)}>
              Create a custom perk
            </Button>
            <Error>
              <>{formik.errors.perksIds}</>
            </Error>
            <DeleteConfirmModal
              title={`Deleting '${deletedCustomPerk?.name}' ...`}
              content={'Are you sure you want to delete this perk? This action cannot be undone.'}
              onDelete={async () => {
                const result = await onDeleteCustomCategory(formik.values.productClassId, deletedCustomPerk?.id)
                if (result.completed) {
                  formik.setFieldValue(
                    'perksIds',
                    formik.values.perksIds.filter((x) => x != result.data),
                  )
                  setDeletedCustomPerk(null)
                }
              }}
              onCancel={() => {
                setDeletedCustomPerk(null)
              }}
              deleteButtonLoading={deleteLoading}
              visible={!!deletedCustomPerk}
            />
          </form>
        )
      }}
    </Formik>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    perksIds: Yup.array().required(translate('admin-dashboard.benefit-details.error.select-perk')),
  })
